import { Show, onMount } from "solid-js"
import { createStore } from "solid-js/store"
import { Progress } from "../components/progress"
import { ImageFileData } from "../components/universal/image-cropper";
import {  ListItem,  LivingArtist } from "../types";
import { A, useNavigate, useRouteData } from "@solidjs/router";
import { fetchPost, scrollToTop } from "../library/common";
import { useForm } from "../components/universal/validation";
import toast from "solid-toast"
import { getApplicationStatusJson, loadStep1Cms } from "../app"
import { CMS } from "../components/cms/cms"
import { ModelDate, ModelEditor, ModelImageCropper, ModelProvider, ModelSelect, ModelTextBox } from "../components/model-form-elements";
import { Select } from "../components/select";
import { PageLoading } from "../components/page-loading";

type State = {
    isContentReady: boolean;
}

type Resources = {
    Countries: ListItem[];
    Genders: ListItem[];
}
type Scholarship = {
    Id: string;
    Name: string;
}

export function Step1 () {
    const [state, setState] = createStore<State>({ isContentReady: false } as State);
    const [resources, setResources] = createStore<Resources>({
        Countries: [],
        Genders: []
    } as Resources);
    const navigate = useNavigate();
    const required = (el: HTMLInputElement) => !el.value && "Please enter a value";
    const minValue = (min: number, el: HTMLInputElement) => parseInt(el.value) < min && `${el.name || "Field"} must be no less than ${min}`;
    const maxValue = (max: number, el: HTMLInputElement) => parseInt(el.value) > max && `${el.name || "Field"} must be no greater than ${max}`;
    const maxLength = (max: number, el: HTMLInputElement) =>
        el.value?.length > max && `This field must be less than ${max} characters in length`;
    const { formSubmit, validate, errors, setErrors } = useForm({ errorClass: "error-input" });
    const [model, setModel] = createStore({
        Id: "",
        Nationality: "",
        Title: "",
        FirstName: "",
        LastName: "",
        Gender: "",
        NationalityId: "",
        BirthDateText: "",
        WebsiteURL: "",
        YearFirstActive: "",
        Galleries: "",
        Awards: "",
        Comment: "",
        CurriculumVitae: "",
        PhotoId: "",
        ProfileImageUrl: ""
    } as LivingArtist);
    const toastError = (err: string) => {
        toast.error(err);
    }
    const isValid = async () => {
        if (model.CurriculumVitaeEditor?.isEmpty()) {
            setErrors("CurriculumVitae", "You must provide a short CV.");
            toastError("You must provide a short CV.");
            return false;
        }
        if (model.CommentEditor?.isEmpty()) {
            setErrors("CurriculumVitae", "You must provide an introductory essay about yourself.");
            toastError("You must provide an introductory essay about yourself.");
            return false;
        }
        const imageFile = await model.PhotoWrapper?.getImageFile();
        console.log("isValid", { imageFile });
        if (!imageFile) {
            toastError("[000] Please attach a profile image.");
            return false;
        }
        if (!imageFile.isValid) {
            toastError("[001] There is an issue with the profile image or its file format. Please ensure it is a JPG or PNG file within the required dimensions.");
            return false;
        }
        return true;
    }
    const getModel = () => {
        return {
            ...model,
            Comment: model.CommentEditor?.getHtml(),
            CurriculumVitae: model.CurriculumVitaeEditor?.getHtml(),
        };
    };
    const getErrors = (obj: any) => Object.keys(obj).filter(key => obj[key].length);
    const saveProfile = async () => {
        const model = getModel();
        console.log("saveProfile", { model });
        const response = await fetchPost("/LivingArtistApplication/SaveProfileJson", model);
        const json = await response.json();
        console.log("saveProfile", { response, json });
        if (!json.Success) {
            return {
                Success: false,
                Errors: json.Errors
            };
        }
        return {
            Success: true,
            Id: json.Data.Id
        };
    };
    const saveProfileImage = async () => {
        const file = (await model.PhotoWrapper?.getImageFile()) as ImageFileData;
        console.log("saveProfileImage", { imageFile: file, isDirty: file?.isDirty });
        if (!file) {
            return toastError("[002] Please attach a profile image.");
        }
        if (!file.isValid) {
            return toastError("[003] The profile image file is not valid. Please check the format and try again.");
        }
        if (!file.isDirty) {
            return;
        }
        const data = {
            LivingArtistId: model.Id,
            NewImageId: model.ProfileImage?.Id,
            ImageFile: file.image,
            OriginalFilename: file.filename
        };
        const response = await fetchPost("/LivingArtistApplication/SaveProfileImageJson", data);
        const json = await response.json();
        if (!json.Success) {
            return toastError("[004] There was an error uploading the profile image. Please try again.")
        }
    };
    const goToStep2 = () => {
        toast.success("Your application profile has been saved.");
        navigate("/step-2");
    };
    const save = async () => {
        console.log("save", { model: getModel() })
        if (!await isValid()){
            return;
        }
        const result = await saveProfile();
        if (!result.Success) {
            return toastError("[005] Please check the form for errors: " + getErrors(result.Errors).join("; "));
        }
        setModel("Id", result.Id);
        await saveProfileImage();
        await getApplicationStatusJson({ forceRefresh: true });
        goToStep2();
    };
    const handleSubmit = async () => {
        await save();
    };
    onMount(async () => {
        const routeData = await useRouteData() as any;
        await loadStep1Cms();
        setResources("Countries", routeData.resources.Countries);
        setResources("Genders", routeData.resources.Genders);
        setModel(routeData.step1);
        if (routeData.step1?.ProfileImage?.URL) {
            setModel("ProfileImage", {
                URL: routeData.step1.ProfileImage.URL
            } as any);
        }
        setState({ isContentReady: true });
        scrollToTop();
    });
    return (
        <Show when={state.isContentReady} fallback={<PageLoading />}>
            <ModelProvider model={model} setModel={setModel} validation={{ errors, validate }}>
                <div class="application" style="font-family: 'Bariol-Regular'; font-size: 1.25em;">
                    <Progress step={1} />
                    <h4 class="salon-heading-md" style="text-align: center;">
                        ARC Living Artist Application
                    </h4>
                    <form use:formSubmit={handleSubmit}>
                        <h4 class="salon-heading-md m-b-lg" style="margin-top: 20px;">
                            <CMS text id="label::personal-details">Personal details</CMS>
                        </h4>
                        <CMS id="24th-scholarship-application/step-1::your-details/description"></CMS>
                        <hr />
                        <div class="row m-t-lg">
                            <div class="col-md-12">
                                <div class="row">
                                    <div class="col-md-1">
                                        <ModelTextBox<LivingArtist>
                                            field="Title"
                                        />
                                    </div>
                                    <div class="col-md-11">
                                        <div class="row">
                                            <div class="col-md-4">
                                                <ModelTextBox<LivingArtist>
                                                    field="FirstName"
                                                    label="First name"
                                                    required
                                                />
                                            </div>
                                            <div class="col-md-4">
                                                <ModelTextBox<LivingArtist>
                                                    field="LastName"
                                                    label="Last name"
                                                    required
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div class="row">
                                    <div class="col-md-2">
                                        <ModelSelect<LivingArtist>
                                            field="Gender"
                                            list={resources.Genders}
                                            required
                                        />
                                    </div>
                                    <div class="col-md-3">
                                        <ModelSelect<LivingArtist>
                                            field="NationalityId"
                                            label="Nationality"
                                            list={resources.Countries}
                                            required
                                        />
                                    </div>
                                </div>
                                <div class="row">
                                    <div class="col-md-4">
                                        <ModelDate<LivingArtist>
                                            field="BirthDateText"
                                            required
                                            label={() =>
                                                <CMS id="label::living-artist-application/date-of-birth">
                                                    Date of birth (mm/dd/yyyy)
                                                    <p>Only the year will be shown to the public</p>
                                                </CMS>
                                            }
                                        />
                                    </div>
                                </div>
                                <div class="row">
                                    <div class="col-md-6">
                                        <h4>
                                            <CMS text id="label::professional-details">Professional Details</CMS>
                                        </h4>
                                        <div style="margin-top: 20px;">
                                            <ModelTextBox<LivingArtist>
                                                    type="URL"
                                                    field="WebsiteURL"
                                                    label="Your website"
                                                    placeholder="https://"
                                            />    
                                        </div>
                                        <div class="row" style="margin-top: 20px;">
                                            <div class="col-md-6">
                                                <ModelTextBox<LivingArtist>
                                                    field="YearFirstActive"
                                                    label={() => <CMS text id="label::living-artist-application/year-first-active">Year first active as an artist</CMS>}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    <div class="col-md-6">
                                        <h4>
                                            <CMS text id="living-artist-application/step-1::profile-photo/heading">Profile photo</CMS>
                                        </h4>
                                        <ModelImageCropper<LivingArtist>
                                            imageUrl="ProfileImageUrl"
                                            editor="PhotoWrapper"    
                                        />
                                    </div>
                                </div>
                                <div class="row">
                                    <div class="col-md-12">
                                        <ModelEditor<LivingArtist>
                                            field="Comment" editor="CommentEditor"
                                            label="Biography"
                                            required error={<>Please enter your biography.</>}
                                        />
                                    </div>
                                </div>
                                <div class="row">
                                    <div class="col-md-12">
                                        <ModelTextBox<LivingArtist>
                                            field="Galleries"
                                            label={() => <CMS id="living-artist-application/step-1::label/galleries">Galleries you have exhibited at <small>(Use a comma to separate them)</small></CMS>}
                                        />
                                    </div>
                                </div>
                                <div class="row">
                                    <div class="col-md-12">
                                        <ModelTextBox<LivingArtist>
                                            field="Awards"
                                            label={() => <CMS id="living-artist-application/step-1::label/awards-or-distinctions">Awards or distinctions you have received <small>(Use a comma to separate them)</small></CMS>}
                                        />
                                    </div>
                                </div>
                                <div class="row">
                                    <div class="col-md-12">
                                        <ModelEditor<LivingArtist>
                                            field="CurriculumVitae" editor="CurriculumVitaeEditor"
                                            label="Curriculum vitae"
                                            required error={<>Please enter your curriculum vitae.</>}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                        <hr />
                        <div class="row m-t-xxl m-b-lg">
                            <div class="col-md-12">
                                <div id="submit-row">
                                    <A href="/introduction" class="btn btn-chunky-lg btn-chunky-dark-grey">
                                        <CMS id="button::back" text>Back</CMS>
                                    </A>
                                    <button type="submit" class="btn btn-chunky-lg btn-chunky-goldenrod">
                                        <CMS text id="button::next">Next</CMS>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </form>
                </div>
            </ModelProvider>
        </Show>
    );
};